import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Trash2, Plus, Minus } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import { useCart } from "../store/CartContext";
import { Button } from "../components/ui/button";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, totalQty } = useCart();

  if (!items.length) {
    return (
      <DahliasShell>
        <section data-testid="cart-page" className="max-w-4xl mx-auto px-5 py-20 text-center">
          <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)]">Your cart is empty.</h1>
          <p className="mt-4 text-lg text-[var(--dahlias-ink)]/70">Take a wander through the shop and pick out a few things.</p>
          <Link
            to="/shop"
            data-testid="cart-shop-link"
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md"
          >
            Visit the shop →
          </Link>
        </section>
      </DahliasShell>
    );
  }

  return (
    <DahliasShell>
      <section data-testid="cart-page" className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-[var(--dahlias-ink)]/70 hover:text-[var(--dahlias-sage-deep)] transition">
          <ChevronLeft className="w-4 h-4" /> Continue shopping
        </Link>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-6 leading-[1.05]">
          Your cart <span className="font-display-italic text-[var(--dahlias-sage-deep)]">({totalQty})</span>
        </h1>

        <div className="mt-8 divide-y divide-[var(--dahlias-sage)]/15 border-t-2 border-b-2 border-[var(--dahlias-sage)]/20" data-testid="cart-items">
          {items.map((l) => {
            const key = `${l.product_id}:${l.variant_id || ""}`;
            return (
              <div key={key} data-testid={`cart-line-${key}`} className="grid grid-cols-12 gap-4 items-center py-5">
                <div
                  className="col-span-3 sm:col-span-2 aspect-square bg-cover bg-center bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/25 wobble-c"
                  style={{ backgroundImage: `url(${l.image_url || "/photos/dahlia-field.jpg"})` }}
                />
                <div className="col-span-9 sm:col-span-5">
                  <Link to={`/shop/${l.product_slug}`} className="font-display text-xl text-[var(--dahlias-forest)] hover:text-[var(--dahlias-sage-deep)]">
                    {l.name}
                  </Link>
                  {l.variant_label && <div className="text-sm text-[var(--dahlias-ink)]/70">{l.variant_label}</div>}
                  <div className="text-sm text-[var(--dahlias-ink)]/60 mt-1">NZ${l.unit_price.toFixed(2)}</div>
                </div>
                <div className="col-span-6 sm:col-span-3 flex items-center gap-2">
                  <div className="inline-flex items-center border-2 border-[var(--dahlias-sage)]/30 wobble-pill overflow-hidden">
                    <button type="button" onClick={() => updateQty(key, l.qty - 1)} className="px-2 py-2 hover:bg-[var(--dahlias-sage)]/10" data-testid={`cart-qty-minus-${key}`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-3 font-medium" data-testid={`cart-qty-${key}`}>{l.qty}</span>
                    <button type="button" onClick={() => updateQty(key, l.qty + 1)} className="px-2 py-2 hover:bg-[var(--dahlias-sage)]/10" data-testid={`cart-qty-plus-${key}`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button type="button" onClick={() => removeItem(key)} className="text-[var(--dahlias-ink)]/50 hover:text-[var(--dahlias-rust)]" data-testid={`cart-remove-${key}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="col-span-6 sm:col-span-2 text-right font-display text-lg text-[var(--dahlias-forest)]">
                  NZ${(l.unit_price * l.qty).toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-base text-[var(--dahlias-ink)]/70">
            Subtotal · <span className="font-display text-[var(--dahlias-forest)] text-2xl">NZ${subtotal.toFixed(2)}</span>
            <div className="text-sm mt-1">Shipping calculated at checkout. Free over $200.</div>
          </div>
          <Link
            to="/checkout"
            data-testid="cart-checkout-btn"
            className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md text-lg"
          >
            Checkout →
          </Link>
        </div>
      </section>
    </DahliasShell>
  );
}
